<template>
    <div>
        <div class="relative z-20">
            <ul
                class="max-lg:w-full lg:max-w-fit flex max-lg:flex-wrap justify-center items-center gap-6 lg:gap-20 h-[calc(var(--offset)*1.75)] mx-auto lg:px-16 rounded-t-3xl relative text-center max-md:mt-4 bg-background"
            >
                <li class="absolute inset-0 pointer-events-none max-md:hidden">
                    <div class="absolute w-full h-20 right-full top-0 rounded-br-3xl outer-round-left"></div>
                    <div class="absolute w-full h-20 left-full top-0 rounded-bl-3xl outer-round-right"></div>
                </li>
                <li v-if="categoryStatus === 'pending'" v-for="item in 3" class="flex flex-col gap-2">
                    <USkeleton class="w-16 h-16 bg-background rounded-full border border-amazon-600" />
                    <USkeleton class="w-full h-4 bg-background" />
                </li>
                <li
                    v-else
                    v-for="(item, index) in categoryList"
                    class="flex flex-col justify-center items-center gap-2 cursor-pointer group w-1/3"
                >
                    <NuxtLink
                        :to="`/category/${item?.slug}`"
                        class="border border-amazon-600 rounded-full p-2 block group-hover:bg-amazon-100 group-hover:ring-4 transition-all ring-0 ring-amazon-500 group-hover:border-transparent"
                    >
                        <UtilsDynamicIcon
                            :icon="item.icon"
                            class-name="w-12 h-12 transition-all text-amazon-500 fill-amazon-500"
                        />
                    </NuxtLink>
                    <NuxtLink :to="`/category/${item?.slug}`">
                        <UiComponentTypography
                            variant="caption"
                            class-name="group-hover:text-amazon-700 transition-all whitespace-nowrap"
                        >
                            {{ item.name }}
                        </UiComponentTypography>
                    </NuxtLink>
                </li>
            </ul>
            <ul class="flex gap-4 my-6 w-full max-sm:overflow-x-auto sm:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <li class="max-sm:min-w-[85%] lg:last:hidden xl:last:block" v-for="item in products">
                    <WidgetsProductCard :is-loading="productStatus === 'pending'" :product="item" />
                </li>
            </ul>
            <UDivider>
                <NuxtLink
                    class="flex items-center gap-2 text-amazon-700 dark:text-sandstone-200 font-light"
                    :to="`/category/${activeCategorySlug}`"
                >
                    {{ $t("more") }}
                    <UIcon name="i-solar-alt-arrow-left-line-duotone" />
                </NuxtLink>
            </UDivider>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { ICategory } from "~/libs/types/api/category";
import type { IPagination } from "~/libs/types/api/pagination";
import type { IProduct } from "~/libs/types/api/product";

const activeCategoryIndex = ref(0);

const {
    data: categoryList,
    status: categoryStatus,
    error: categoryError,
    refresh: categoryRefresh,
} = await useCategoryList<ICategory[]>();

const activeCategorySlug = computed(() => {
    if (categoryList.value) {
        return categoryList.value[activeCategoryIndex.value]?.slug;
    }
    return "all";
});

const {
    data: productList,
    status: productStatus,
    error: productError,
    refresh: productRefresh,
} = await useLazyAsyncData<IPagination<IProduct[]>>(
    `${activeCategorySlug.value}-products`,
    () =>
        fetchLocal("/products", {
            params: {
                filter: {
                    "category.slug": activeCategorySlug.value,
                },
            },
        }),
    {
        watch: [activeCategorySlug],
        dedupe: "cancel",
    }
);

const products = computed(() => {
    return productList.value?.data.slice(0, 4);
});
</script>

<style scoped>
.outer-round-right {
    box-shadow: -0.5rem 1.5rem 0 #f5f2ec;
}
.outer-round-left {
    box-shadow: 0.5rem 1.5rem 0 #f5f2ec;
}
.dark .outer-round-right {
    box-shadow: -0.5rem 1.5rem 0 theme("colors.sandstone.900");
}
.dark .outer-round-left {
    box-shadow: 0.5rem 1.5rem 0 theme("colors.sandstone.900");
}
</style>
